import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  IconButton,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  useTheme,
  CircularProgress,
  Alert,
  Container,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const QuestionDetail = () => {
  const theme = useTheme();
  const { questionId } = useParams();
  const navigate = useNavigate();
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchQuestion = async () => {
      if (!questionId) {
        setError('No question ID provided');
        setLoading(false);
        return;
      }

      try {
        const docRef = doc(db, 'wrongAnswerJournal', questionId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setQuestion({ id: docSnap.id, ...docSnap.data() });
        } else {
          setError('Question not found');
        }
      } catch (err) {
        console.error('Error fetching question:', err);
        setError('Failed to load question details');
      } finally {
        setLoading(false);
      }
    };

    fetchQuestion();
  }, [questionId]);

  const handleBack = () => {
    navigate('/app/journal');
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <IconButton onClick={handleBack} color="primary">
          <ArrowBack /> Back to Journal
        </IconButton>
      </Container>
    );
  }

  if (!question) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="info" sx={{ mb: 2 }}>Question not found</Alert>
        <IconButton onClick={handleBack} color="primary">
          <ArrowBack /> Back to Journal
        </IconButton>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
      >
        <Paper elevation={3} sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <IconButton onClick={handleBack}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h5">
              Question Details
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {/* Question Info */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Question Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Question Number
                      </Typography>
                      <Typography variant="body1">
                        {question.questionNumber || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Date Added
                      </Typography>
                      <Typography variant="body1">
                        {question.date ? new Date(question.date).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Question Image */}
            {question.imageUrl && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Question Image
                    </Typography>
                    <Box
                      component="img"
                      src={question.imageUrl}
                      alt="LSAT Question"
                      sx={{
                        width: '100%',
                        maxHeight: '400px',
                        objectFit: 'contain',
                        borderRadius: 1,
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* AI Analysis */}
            {(question.details?.aiAnalysis || question.explanation) && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Analysis
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" color="primary" gutterBottom>
                        Question Type
                      </Typography>
                      <Typography variant="body1">
                        {question.details?.aiAnalysis?.questionType || question.questionType || 'N/A'}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" color="primary" gutterBottom>
                        Summary
                      </Typography>
                      <Typography variant="body1">
                        {question.details?.aiAnalysis?.summary || question.explanation || 'N/A'}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box>
                      <Typography variant="subtitle1" color="primary" gutterBottom>
                        Notes
                      </Typography>
                      <Typography 
                        variant="body1"
                        sx={{ 
                          whiteSpace: 'pre-wrap',
                          fontFamily: 'inherit',
                        }}
                      >
                        {question.details?.aiAnalysis?.answerAnalysis || question.notes || 'N/A'}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Chat History */}
            {question.details?.chatHistory?.length > 0 && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Discussion History
                    </Typography>
                    <List>
                      {question.details.chatHistory.map((chat, index) => (
                        <ListItem key={index} divider={index !== question.details.chatHistory.length - 1}>
                          <ListItemText
                            primary={chat.role === 'user' ? 'You' : 'AI Tutor'}
                            secondary={chat.content}
                            primaryTypographyProps={{
                              color: chat.role === 'user' ? 'primary' : 'secondary',
                              fontWeight: 500,
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Paper>
      </motion.div>
    </Container>
  );
};

export default QuestionDetail; 